import React, { useContext, useEffect, useState } from "react";
import PhoneInput, { CountryData } from "react-phone-input-2";
import { PHONE_NUMBER, PhoneCountryCodeKeys, PhoneValidationRegexKeys } from "../../../constants/global";
import { isEmail } from "../../../helpers/Utils";
import { IActivateBasketFullModel } from "../../../models/ActivateGiftCard.model";
import { AppSettingContext } from "../../Application";

interface IBillingDetailsStepProps {
    cacheBasket: IActivateBasketFullModel,
    handleSetBillingDetails(billingDetails: IBillingDetails, isValid: boolean): void;
    handleSetCurrentStep(): void;
}

interface IBillingDetails {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

const BillingDetailsStep = (props: IBillingDetailsStepProps) => {
    const { labels } = useContext(AppSettingContext);

    const phoneInputDropdowns = document.querySelectorAll('.flag-dropdown') as NodeListOf<HTMLElement>;
    const phoneNumberErrorMessages = document.querySelectorAll('.invalid-number-message') as NodeListOf<HTMLElement>;

    //#region STATE PROPERTIES

    const [billingDetails, setBillingDetails] = useState({
        firstName: props.cacheBasket?.firstName ?? "",
        lastName: props.cacheBasket?.lastName ?? "",
        email: props.cacheBasket?.email ?? "",
        phone: props.cacheBasket?.phone ?? ""
    });
    const [errors, setErrors] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: ""
    });

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        if (phoneInputDropdowns && phoneInputDropdowns.length > 0) {
            phoneInputDropdowns.forEach(dropdown => {
                dropdown.style.borderColor = '#6b7280';
                dropdown.style.borderRightColor = '#cacaca';
            });
        }

        if (phoneNumberErrorMessages && phoneNumberErrorMessages.length > 0) {
            phoneNumberErrorMessages.forEach(errorMessage => {
                errorMessage.style.left = '0';
                errorMessage.style.top = '42px';
                errorMessage.classList.add("react-input-error-message");
            });
        }
    }, [phoneInputDropdowns, phoneNumberErrorMessages]);

    useEffect(() => {
        formValidations.validateAllFields();
    }, [errors]);

    useEffect(() => {
        billingDetails.firstName && formValidations.validateFirstName();
        billingDetails.lastName && formValidations.validateLastName();
        billingDetails.phone && formValidations.validatePhone();
    }, [billingDetails]);

    useEffect(() => {
        billingDetails.email && formValidations.validateEmail(null);
    }, [billingDetails.email]);

    //#endregion

    //#region HANDLERS

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        props.handleSetCurrentStep();

        const { name, value } = event.target;
        let formattedValue = value;
        let error = "";

        if (name === "firstName") {
            formattedValue = formattedValue
                .replace(/[^a-zA-Z'-]/g, '')
                .trim();

            error = labels.get("GiftCard.App.ActivateLoadCard.Errors.FirstName");
        } else if (name === "lastName") {
            formattedValue = formattedValue
                .replace(/[^a-zA-Z'-]/g, '')
                .trim();

            error = labels.get("GiftCard.App.ActivateLoadCard.Errors.LastName");
        } else if (name === "email") {
            formattedValue = formattedValue
                .replace(/[^a-zA-Z0-9._%+-@]/g, '')
                .replace(/[?/;:,<>=]/g, '')
                .trim();

            error = labels.get("GiftCard.App.ActivateLoadCard.Errors.Email.Required");
        }

        if (!formattedValue) {
            setErrors({
                ...errors,
                [name]: error
            });
        }

        setBillingDetails({
            ...billingDetails,
            [name]: formattedValue
        });
    };

    //#endregion

    //#region HELPERS

    const formValidations = {
        validateAllFields: () => {
            const isFormValid =
                billingDetails.firstName && !errors.firstName &&
                billingDetails.lastName && !errors.lastName &&
                billingDetails.email && !errors.email &&
                !errors.phone;

            props.handleSetBillingDetails(billingDetails, isFormValid);
        },

        validateFirstName: () => {
            const value = billingDetails.firstName.trim();
            let error = "";

            if (!value) {
                error = labels.get("GiftCard.App.ActivateLoadCard.Errors.FirstName");
            }

            setErrors(currErrors => ({
                ...currErrors,
                firstName: error
            }));
        },
        validateLastName: () => {
            const value = billingDetails.lastName.trim();
            let error = "";

            if (!value) {
                error = labels.get("GiftCard.App.ActivateLoadCard.Errors.LastName");
            }

            setErrors(currErrors => ({
                ...currErrors,
                lastName: error
            }));
        },
        validateEmail: (event: React.FocusEvent<HTMLInputElement, Element>) => {
            const value = billingDetails.email.trim();
            let error = "";

            if (!value) {
                error = labels.get("GiftCard.App.ActivateLoadCard.Errors.Email.Required");
            } else if (!isEmail(value)) {
                if (event && event.type === "blur") {
                    error = labels.get("GiftCard.App.ActivateLoadCard.Errors.Email.Invalid");
                } else {
                    error = "still invalid";
                }
            }

            setErrors(currErrors => ({
                ...currErrors,
                email: error
            }));
        },
        validatePhone: () => {
            const value = billingDetails.phone.trim();
            let error = "";

            const allowedCountryCodes = [] as string[];
            for (const key in PHONE_NUMBER.COUNTRIES) {
                allowedCountryCodes.push(PHONE_NUMBER.COUNTRIES[key as PhoneCountryCodeKeys])
            }

            if (!value || allowedCountryCodes.includes(value)) {
                error = "";
            } else {
                if (PHONE_NUMBER.VALIDATIONS.REGEX.IE.test(value)) {
                    error = "";
                } else {
                    let isValid = true;

                    for (const regexKey in PHONE_NUMBER.VALIDATIONS.REGEX) {
                        const currRegex = PHONE_NUMBER.VALIDATIONS.REGEX[regexKey as PhoneValidationRegexKeys];
                        isValid = currRegex.test(value);
                    }

                    if (!isValid) {
                        error = labels.get("GiftCard.App.ActivateLoadCard.Errors.Phone");
                    }
                }
            }

            setErrors(currErrors => ({
                ...currErrors,
                phone: error
            }));
        }
    };

    //#endregion

    return (
        <div className="load-billingDetails">
            <h4>{labels.get("GiftCard.App.ActivateLoadCard.Steps.BillingDetails")}</h4>
            <div className="form-field-group">
                <div className="form-field">
                    <label htmlFor="load-firstName" className="label-fs">
                        {labels.get("GiftCard.App.ActivateLoadCard.Labels.FirstName")}
                    </label>
                    <input type="text"
                        value={billingDetails.firstName}
                        onChange={(event) => handleChange(event)}
                        maxLength={150}
                        name="firstName"
                        id="load-firstName"
                        placeholder={labels.get("GiftCard.App.ActivateLoadCard.Placeholders.FirstName")}
                        className={errors.firstName ? "react-input-error" : ""}
                    />
                    {errors.firstName &&
                        <div className="react-input-error-message">
                            {errors.firstName}
                        </div>
                    }
                </div>
                <div className="form-field">
                    <label htmlFor="load-lastName" className="label-fs">
                        {labels.get("GiftCard.App.ActivateLoadCard.Labels.LastName")}
                    </label>
                    <input type="text"
                        value={billingDetails.lastName}
                        onChange={(event) => handleChange(event)}
                        maxLength={150}
                        name="lastName"
                        id="load-lastName"
                        placeholder={labels.get("GiftCard.App.ActivateLoadCard.Placeholders.LastName")}
                        className={errors.lastName ? "react-input-error" : ""}
                    />
                    {errors.lastName &&
                        <div className="react-input-error-message">
                            {errors.lastName}
                        </div>
                    }
                </div>
            </div>
            <div className="form-field-group">
                <div className="form-field">
                    <label htmlFor="load-email" className="label-fs">
                        {labels.get("GiftCard.App.ActivateLoadCard.Labels.Email")}
                    </label>
                    <input type="text"
                        value={billingDetails.email}
                        onChange={(event) => handleChange(event)}
                        maxLength={150}
                        name="email"
                        id="load-email"
                        placeholder={labels.get("GiftCard.App.ActivateLoadCard.Placeholders.Email")}
                        className={(errors.email && errors.email !== "still invalid") ? "react-input-error" : ""}
                        onBlur={(event) => formValidations.validateEmail(event)}
                    />
                    {(errors.email && errors.email !== "still invalid") &&
                        <div className="react-input-error-message">
                            {errors.email}
                        </div>
                    }
                </div>
                <div className="form-field">
                    <label htmlFor="recipients__phone" className="label-fs">
                        {labels.get("GiftCard.App.ActivateLoadCard.Labels.Phone")}
                    </label>
                    <div className="recipients__phone">
                        <PhoneInput
                            placeholder={labels.get("GiftCard.App.ActivateLoadCard.Placeholders.Phone")}
                            country={PHONE_NUMBER.DEFAULT_COUNTRY.toLowerCase()}
                            onlyCountries={Object.keys(PHONE_NUMBER.COUNTRIES).map(c => c.toLowerCase())}
                            countryCodeEditable={false}
                            value={billingDetails.phone}
                            onChange={(phone) => {
                                props.handleSetCurrentStep();

                                if (phone.length > PHONE_NUMBER.VALIDATIONS.MAXLENGTH) {
                                    phone = phone.slice(0, PHONE_NUMBER.VALIDATIONS.MAXLENGTH)
                                }

                                setBillingDetails({
                                    ...billingDetails,
                                    phone: `+${phone}`
                                });
                            }}
                            isValid={(inputNumber, data: CountryData) => {
                                const allowedCountryCodes = [] as string[];
                                for (const key in PHONE_NUMBER.COUNTRIES) {
                                    allowedCountryCodes.push(PHONE_NUMBER.COUNTRIES[key as PhoneCountryCodeKeys])
                                }

                                if (!billingDetails.phone || allowedCountryCodes.includes(billingDetails.phone)) {
                                    return true;
                                }

                                const allowedCountriesAbbr = Object.keys(PHONE_NUMBER.COUNTRIES).map(c => c.toLowerCase());
                                if (allowedCountriesAbbr.includes(data.iso2.toLowerCase())) {
                                    const regexKey = data.iso2.toUpperCase() as PhoneValidationRegexKeys;
                                    const regex = PHONE_NUMBER.VALIDATIONS.REGEX[regexKey] as RegExp;

                                    if (regex) {
                                        return regex.test(billingDetails.phone);
                                    }
                                }

                                return false;
                            }}
                            inputClass='react-phone-input'
                            enableLongNumbers={PHONE_NUMBER.VALIDATIONS.MAXLENGTH}
                            defaultErrorMessage={labels.get("GiftCard.App.ActivateLoadCard.Errors.Phone")}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BillingDetailsStep;